import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate } from 'react-router-dom';
import { 
  getListings, 
  approveListing, 
  rejectListing, 
  featureListing, 
  unfeatureListing, 
  deleteListing,
  updateListingModerationStatus,
  bulkModerateListings,
  exportListings,
  IMAGE_BASE_URL
} from '../../services/api';
import ListingFilters from '../../components/listings/ListingFilters';
import ListingCard from '../../components/listings/ListingCard';
import ListingStats from '../../components/listings/ListingStats';
import BulkActions from '../../components/listings/BulkActions';

const Listings = () => {
  const [listings, setListings] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [selectedListings, setSelectedListings] = useState([]);
  const [bulkLoading, setBulkLoading] = useState(false);
  
  const [filters, setFilters] = useState({
    search: '',
    status: '',
    moderation_status: '',
    category_id: '',
    state_id: '',
    lga_id: '',
    lcda_id: '',
    is_boosted: '',
    is_featured: '',
    min_price: '',
    max_price: '',
    date_from: '',
    date_to: '',
    order_by: 'created_at',
    order_direction: 'DESC'
  });
  
  const [pagination, setPagination] = useState({
    page: 1,
    limit: 20,
    total: 0,
    pages: 0
  });

  const navigate = useNavigate();

  const fetchListings = useCallback(async () => {
    try {
      setLoading(true);
      setError('');
      
      const params = {
        page: pagination.page,
        limit: pagination.limit,
        ...Object.fromEntries(
          Object.entries(filters).filter(([_, value]) => 
            value !== '' && value !== null && value !== undefined
          )
        )
      };

      const response = await getListings(params);
      
      setListings(response.data.listings || []);
      setPagination(prev => ({
        ...prev,
        total: response.data.pagination?.total || 0,
        pages: response.data.pagination?.pages || 0
      }));
      
    } catch (err) {
      setError('Failed to load listings: ' + (err.message || 'Unknown error'));
      console.error('Fetch listings error:', err);
    } finally {
      setLoading(false);
    }
  }, [filters, pagination.page, pagination.limit]);

  useEffect(() => {
    fetchListings();
  }, [fetchListings]);

  const handleFilterChange = (newFilters) => {
    setFilters(newFilters);
    setPagination(prev => ({ ...prev, page: 1 }));
    setSelectedListings([]);
  };

  const handlePageChange = (newPage) => {
    setPagination(prev => ({ ...prev, page: newPage }));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleSelectListing = (listingId) => {
    setSelectedListings(prev => {
      if (prev.includes(listingId)) {
        return prev.filter(id => id !== listingId);
      } else {
        return [...prev, listingId];
      }
    });
  };

  const handleSelectAll = () => {
    if (selectedListings.length === listings.length) {
      setSelectedListings([]);
    } else {
      setSelectedListings(listings.map(l => l.listing_id));
    }
  };

  const handleListingAction = async (listingId, action, reason = '') => {
    let confirmationMessage = `Are you sure you want to ${action} this listing?`;
    
    if (action === 'reject' || action === 'delete') {
      const userReason = prompt(
        action === 'reject' 
          ? 'Please provide a reason for rejection:' 
          : 'Please provide a reason for deletion:',
        action === 'reject' ? 'Does not meet marketplace guidelines' : 'Violation of marketplace policies'
      );
      if (userReason === null) return;
      reason = userReason;
    }

    if (action === 'update_moderation_status') {
      const status = prompt('Enter new moderation status (pending, approved, rejected, flagged):', 'pending');
      if (!status || !['pending', 'approved', 'rejected', 'flagged'].includes(status)) {
        setError('Invalid status. Must be: pending, approved, rejected, or flagged');
        return;
      }
      const notes = prompt('Admin notes (optional):', '');
      
      if (!window.confirm(`Change moderation status to ${status}?`)) return;
      
      try {
        await updateListingModerationStatus(listingId, status, notes || '');
        fetchListings();
      } catch (err) {
        setError(`Failed to update status: ` + err.message);
      }
      return;
    }

    if (!window.confirm(confirmationMessage)) {
      return;
    }

    try {
      switch (action) {
        case 'approve':
          await approveListing(listingId);
          break;
        case 'reject':
          await rejectListing(listingId, reason);
          break;
        case 'feature':
          await featureListing(listingId);
          break;
        case 'unfeature':
          await unfeatureListing(listingId);
          break;
        case 'delete':
          await deleteListing(listingId, reason);
          break;
        default:
          break;
      }
      
      fetchListings();
    } catch (err) {
      setError(`Failed to ${action} listing: ` + err.message);
    }
  };

  const handleBulkAction = async (action, reason = '') => {
    if (selectedListings.length === 0) {
      setError('Please select at least one listing');
      return;
    }

    let confirmationMessage = `Are you sure you want to ${action} ${selectedListings.length} listing(s)?`;
    
    if (action === 'reject' || action === 'delete') {
      const userReason = prompt(
        `Please provide a reason for ${action}:`, 
        action === 'reject' ? 'Does not meet marketplace guidelines' : 'Violation of marketplace policies'
      );
      if (userReason === null) return;
      reason = userReason;
    }

    if (!window.confirm(confirmationMessage)) {
      return;
    }

    try {
      setBulkLoading(true);
      await bulkModerateListings(selectedListings, action, reason);
      setSelectedListings([]);
      fetchListings();
    } catch (err) {
      setError(`Failed to perform bulk action: ${err.message}`);
    } finally {
      setBulkLoading(false);
    }
  };

  const handleExport = async () => {
    try {
      setError('');
      const response = await exportListings(filters);
      
      // Create download link
      const url = window.URL.createObjectURL(response.blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = response.filename || 'listings_export.csv';
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
      
    } catch (err) {
      setError('Failed to export listings: ' + err.message);
    }
  };

  const refreshListings = () => {
    fetchListings();
    setSelectedListings([]);
  };

  // Loading state
  if (loading && listings.length === 0) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-900">
        <div className="text-center">
          <div className="w-16 h-16 border-4 border-[#ff660e] border-t-transparent rounded-full animate-spin mx-auto mb-6"></div>
          <h3 className="text-xl font-semibold text-white mb-2">Loading Listings</h3>
          <p className="text-gray-400">Fetching your listing data...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-900 p-4 md:p-6 lg:p-8">
      {/* Header */}
      <div className="mb-8">
        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4 mb-6">
          <div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-2">
              Listing <span className="text-[#ff660e]">Management</span>
            </h1>
            <p className="text-gray-400">Manage and moderate product listings across the platform</p>
          </div>
          
          <div className="flex flex-wrap gap-3">
            <button
              onClick={() => handleFilterChange({ ...filters, moderation_status: 'pending' })}
              className="px-5 py-2.5 rounded-xl font-medium text-white transition-all duration-200 hover:shadow-lg hover:scale-[1.02] flex items-center gap-2 group bg-gradient-to-r from-[#ff660e] to-orange-500"
            >
              <span className="group-hover:rotate-12 transition-transform">👁️</span>
              <span>Pending Reviews</span>
            </button>
            
            <button
              onClick={handleExport}
              className="px-5 py-2.5 rounded-xl font-medium text-white transition-all duration-200 hover:shadow-lg hover:scale-[1.02] flex items-center gap-2 group bg-gradient-to-r from-[#ff660e] to-orange-500"
            >
              <span className="group-hover:scale-110 transition-transform">📥</span>
              <span>Export CSV</span>
            </button>
            
            <button
              onClick={refreshListings}
              className="px-5 py-2.5 rounded-xl font-medium border-2 transition-all duration-200 hover:shadow hover:scale-[1.02] flex items-center gap-2 group"
              style={{ 
                borderColor: '#ff660e',
                color: '#ff660e',
                backgroundColor: 'transparent'
              }}
            >
              <span className="group-hover:rotate-180 transition-transform duration-500">🔄</span>
              <span>Refresh</span>
            </button>
          </div>
        </div>

        {/* Error Alert */}
        {error && (
          <div className="mb-6 p-4 rounded-xl border border-red-700 bg-red-900/20 backdrop-blur-sm flex items-center justify-between animate-fadeIn">
            <div className="flex items-center gap-3">
              <div className="p-2 rounded-lg bg-red-800/30">
                <span className="text-red-400">⚠️</span>
              </div>
              <p className="text-red-300 font-medium">{error}</p>
            </div>
            <button 
              onClick={() => setError('')}
              className="text-red-400 hover:text-red-300 p-1 rounded-full hover:bg-red-800/30 transition-colors"
            >
              ✕
            </button>
          </div>
        )}
      </div>

      {/* Filters Component */}
      <div className="mb-8">
        <ListingFilters 
          filters={filters}
          onFilterChange={handleFilterChange}
          onRefresh={refreshListings}
        />
      </div>

      {/* Bulk Actions Component */}
      {selectedListings.length > 0 && (
        <div className="mb-6 animate-slideDown">
          <BulkActions 
            selectedCount={selectedListings.length}
            totalCount={listings.length}
            onSelectAll={handleSelectAll}
            onBulkAction={handleBulkAction}
            isLoading={bulkLoading}
            isAllSelected={selectedListings.length === listings.length && listings.length > 0}
          />
        </div>
      )}

      {/* Stats Component */}
      <div className="mb-8">
        <ListingStats 
          listings={listings}
          pagination={pagination}
          filters={filters}
        />
      </div>

      {/* Listings Grid */}
      <div className="mb-10">
        {listings.length > 0 ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-6">
            {listings.map((listing) => (
              <ListingCard
                key={listing.listing_id}
                listing={listing}
                isSelected={selectedListings.includes(listing.listing_id)}
                onSelect={() => handleSelectListing(listing.listing_id)}
                onViewDetails={() => navigate(`/admin/listings/${listing.listing_id}`)}
                onAction={(action, reason) => handleListingAction(listing.listing_id, action, reason)}
                onRefresh={refreshListings}
                imageBaseUrl={IMAGE_BASE_URL}
              />
            ))}
          </div>
        ) : (
          <div className="text-center py-16 px-4 rounded-2xl bg-gray-800 border-2 border-dashed border-gray-700 backdrop-blur-sm">
            <div className="w-24 h-24 mx-auto mb-6 rounded-full flex items-center justify-center bg-gray-700 group">
              <span className="text-4xl group-hover:scale-110 transition-transform">📦</span>
            </div>
            <h3 className="text-2xl font-bold text-white mb-3">No Listings Found</h3>
            <p className="text-gray-400 max-w-md mx-auto mb-8">
              Try adjusting your search filters or create new listings to get started.
            </p>
            <button
              onClick={() => handleFilterChange({
                search: '',
                status: '',
                moderation_status: '',
                category_id: '',
                state_id: '',
                lga_id: '',
                lcda_id: '',
                is_boosted: '',
                is_featured: '',
                min_price: '',
                max_price: '',
                date_from: '',
                date_to: '',
                order_by: 'created_at',
                order_direction: 'DESC'
              })}
              className="px-6 py-3 rounded-xl font-medium text-white transition-all duration-200 hover:shadow-lg hover:scale-[1.02] bg-gradient-to-r from-[#ff660e] to-orange-500"
            >
              Reset All Filters
            </button>
          </div>
        )}
      </div>

      {/* Pagination */}
      {pagination.pages > 1 && (
        <div className="bg-gray-800 rounded-xl shadow-lg border border-gray-700 p-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
            <div className="text-sm text-gray-300">
              <span className="font-semibold text-white">Showing {((pagination.page - 1) * pagination.limit) + 1}</span> to{' '}
              <span className="font-semibold text-white">
                {Math.min(pagination.page * pagination.limit, pagination.total)}
              </span>{' '}
              of <span className="font-semibold text-white">{pagination.total}</span> listings
            </div>
            
            <div className="flex items-center gap-2">
              <button
                onClick={() => handlePageChange(pagination.page - 1)}
                disabled={pagination.page === 1}
                className="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-300 bg-gray-700 border border-gray-600 hover:bg-gray-600 hover:border-[#ff660e] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 hover:scale-[1.02]"
              >
                <span>←</span>
                <span>Previous</span>
              </button>
              
              <div className="flex items-center gap-1">
                {Array.from({ length: Math.min(5, pagination.pages) }, (_, i) => {
                  const pageNum = i + 1;
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePageChange(pageNum)}
                      className={`w-10 h-10 rounded-lg text-sm font-medium transition-all duration-200 hover:scale-105 ${
                        pagination.page === pageNum
                          ? 'text-white shadow-lg'
                          : 'text-gray-300 hover:bg-gray-700 hover:text-white'
                      }`}
                      style={{
                        backgroundColor: pagination.page === pageNum ? '#ff660e' : 'transparent'
                      }}
                    >
                      {pageNum}
                    </button>
                  );
                })}
                {pagination.pages > 5 && (
                  <span className="px-3 text-gray-500">...</span>
                )}
              </div>
              
              <button
                onClick={() => handlePageChange(pagination.page + 1)}
                disabled={pagination.page === pagination.pages}
                className="px-4 py-2.5 rounded-lg text-sm font-medium text-gray-300 bg-gray-700 border border-gray-600 hover:bg-gray-600 hover:border-[#ff660e] transition-all duration-200 disabled:opacity-50 disabled:cursor-not-allowed flex items-center gap-2 hover:scale-[1.02]"
              >
                <span>Next</span>
                <span>→</span>
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Add custom CSS for animations */}
      <style jsx>{`
        @keyframes fadeIn {
          from { opacity: 0; transform: translateY(-10px); }
          to { opacity: 1; transform: translateY(0); }
        }
        @keyframes slideDown {
          from { transform: translateY(-20px); opacity: 0; }
          to { transform: translateY(0); opacity: 1; }
        }
        .animate-fadeIn {
          animation: fadeIn 0.3s ease-out;
        }
        .animate-slideDown {
          animation: slideDown 0.3s ease-out;
        }
      `}</style>
    </div>
  );
};

export default Listings;